// catalog-correlation.tsx — cluster « matrice de corrélation » de l'édition catalogue.
// Refonte « Carte de chaleur » (DBU) : densité 10×, la COULEUR porte la valeur (teinte unique
// --primary, intensité croissante — pas d'arc-en-ciel, pas de rouge/vert), familles en blocs,
// édition dans le VOLET LATÉRAL (plus en cellule). Composant public : CorrelationMatrixSection.

import { ChevronRight, Folder } from 'lucide-react';
import * as React from 'react';

import type { CorrelationEdgeView, ProfileView } from '@/api/catalog-client';
import { useCatalogEdges, useCatalogProfiles, useUpsertEdge } from '@/api/use-catalog-edition';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  correlationPercentToWeight,
  correlationWeightToPercent,
  edgeKey,
  MAJOR_CORRELATION_PERCENT,
  saveCsv,
} from './catalog-edition-helpers';
import type { CatalogMatrixView } from './catalog-edition-screen';
import { groupProfilesByFamily } from './catalog-profile-tree';

// -----------------------------------------------------------------------------
// Échelle de couleur — teinte unique (--primary), intensité croissante (DBU).
// -----------------------------------------------------------------------------
// fond(v) = color-mix(in oklab, --primary calc(14% + v×0.86%), crème) ; le plancher de 14 %
// garantit qu'une valeur de 1 % reste distinguable d'une cellule non évaluée. Contraste du
// texte des valeurs vérifié ≥ 4,5:1 (blanc au-delà de 62 %, encre translucide en-deçà).

const CELL_UNEVALUATED = '#f6f4f0';
const CELL_CREAM = '#f7f4ef';
const DIAGONAL_HATCH =
  'repeating-linear-gradient(45deg, #e9e5dd 0, #e9e5dd 2px, #f4f1ec 2px, #f4f1ec 4px)';

/** Fond d'une cellule : `null` (non évaluée) → crème neutre ; sinon teinte --primary à intensité v. */
function cellBackground(percent: number | null): string {
  if (percent === null) {
    return CELL_UNEVALUATED;
  }
  const mix = 14 + percent * 0.86;
  return `color-mix(in oklab, hsl(var(--primary)) ${String(mix)}%, ${CELL_CREAM})`;
}

/** Couleur du texte de valeur d'une cellule : blanc au-delà de 62 %, encre translucide sinon. */
function cellTextColor(percent: number): string {
  return percent > 62 ? '#fff' : 'rgba(0,0,0,.6)';
}

/** Code court de famille dérivé (1er mot, 4 caractères). Substituable par un champ dédié plus tard. */
function familyShortCode(famille: string): string {
  const first = famille.replace(/[^\p{L}]+/gu, ' ').trim().split(/\s+/)[0] ?? famille;
  return first.slice(0, 4).toUpperCase();
}

/** Teinte catégorielle stable du LIBELLÉ de code famille (le bandeau, jamais les valeurs). */
function familyLabelColor(index: number): string {
  return `hsl(${String((index * 67) % 360)} 48% 35%)`;
}

/** En-tête vertical de colonne : les DEUX derniers chiffres du code (progressifs dans la famille). */
function codeSuffix(code: string): string {
  const digits = code.replace(/\D/gu, '');
  return digits.length >= 2 ? digits.slice(-2) : digits || code.slice(-2);
}

/** Libellé accessible du degré (nombre de corrélations distinctes d'un profil). */
function degreeLabel(count: number): string {
  if (count <= 0) {
    return 'aucune corrélation';
  }
  return count > 1 ? `${String(count)} corrélations` : '1 corrélation';
}

/** Groupe une famille (libellé + profils aplatis sur les sous-familles) pour l'ordre matriciel. */
interface MatrixFamilyGroup {
  readonly famille: string;
  readonly profiles: readonly ProfileView[];
}

interface CellRef {
  readonly from: string;
  readonly to: string;
}

function sameCell(a: CellRef | null, b: CellRef | null): boolean {
  return a !== null && b !== null && a.from === b.from && a.to === b.to;
}

export function CorrelationMatrixSection({
  catalogVersionId,
  view,
  onViewChange,
}: {
  readonly catalogVersionId: string;
  readonly view: CatalogMatrixView;
  readonly onViewChange: (next: CatalogMatrixView) => void;
}): React.JSX.Element {
  const profilesQuery = useCatalogProfiles(catalogVersionId);
  const edgesQuery = useCatalogEdges(catalogVersionId);

  const profiles = React.useMemo<readonly ProfileView[]>(
    () => profilesQuery.data ?? [],
    [profilesQuery.data],
  );
  // DBU §2.4 l.983 : la matrice ACTIVE MASQUE les arêtes `Archived` CÔTÉ FRONT (l'API expose l'état
  // en lecture mais ne filtre PAS). Seules les arêtes Active alimentent grille, degré, export, corrélés.
  const edges = React.useMemo<readonly CorrelationEdgeView[]>(
    () => (edgesQuery.data ?? []).filter((e) => e.state === 'Active'),
    [edgesQuery.data],
  );

  // Index orienté (lookup O(1) par cellule) + nombre de CORRÉLATIONS TROUVÉES par profil (voisins
  // DISTINCTS, deux orientations fusionnées — jamais compté 2× pour une paire bidirectionnelle).
  const { edgeIndex, degree } = React.useMemo(() => {
    const idx = new Map<string, CorrelationEdgeView>();
    const neighbors = new Map<string, Set<string>>();
    const link = (from: string, to: string): void => {
      let set = neighbors.get(from);
      if (set === undefined) {
        set = new Set<string>();
        neighbors.set(from, set);
      }
      set.add(to);
    };
    for (const edge of edges) {
      idx.set(edgeKey(edge.profileFrom, edge.profileTo), edge);
      link(edge.profileFrom, edge.profileTo);
      link(edge.profileTo, edge.profileFrom);
    }
    const deg = new Map<string, number>();
    for (const [code, set] of neighbors) {
      deg.set(code, set.size);
    }
    return { edgeIndex: idx, degree: deg };
  }, [edges]);

  const byCode = React.useMemo(() => {
    const m = new Map<string, ProfileView>();
    for (const p of profiles) {
      m.set(p.profileCode, p);
    }
    return m;
  }, [profiles]);

  // Familles présentes (filtre l.908) — tri stable pour un rendu déterministe.
  const familles = React.useMemo(
    () => [...new Set(profiles.map((p) => p.famille))].sort((a, b) => a.localeCompare(b)),
    [profiles],
  );

  // Profils VISIBLES (filtre famille + « masquer sans corrélation ») — réduit la grille.
  const visibleProfiles = React.useMemo(
    () =>
      profiles.filter(
        (p) =>
          (view.famille === undefined || p.famille === view.famille) &&
          (view.actives !== true || (degree.get(p.profileCode) ?? 0) > 0),
      ),
    [profiles, view.famille, view.actives, degree],
  );

  // Ordre matriciel CONTIGU par famille (mêmes lignes et colonnes) : blocs famille cohérents.
  const familyGroups = React.useMemo<readonly MatrixFamilyGroup[]>(
    () =>
      groupProfilesByFamily(visibleProfiles).map((f) => ({
        famille: f.famille,
        profiles: f.sousFamilles.flatMap((sf) => sf.profiles),
      })),
    [visibleProfiles],
  );
  const orderedProfiles = React.useMemo<readonly ProfileView[]>(
    () => familyGroups.flatMap((g) => g.profiles),
    [familyGroups],
  );

  const getPercent = React.useCallback(
    (from: string, to: string): number | null => {
      const edge = edgeIndex.get(edgeKey(from, to));
      return edge !== undefined ? correlationWeightToPercent(edge.weight) : null;
    },
    [edgeIndex],
  );

  // Paires renseignées (comptage NON orienté : A↔B compte 1, pas 2).
  const filledPairs = React.useMemo(() => {
    const s = new Set<string>();
    for (const e of edges) {
      const pair = [e.profileFrom, e.profileTo].sort((a, b) => a.localeCompare(b));
      s.add(`${pair[0] ?? ''}::${pair[1] ?? ''}`);
    }
    return s.size;
  }, [edges]);

  const visibleCodes = React.useMemo(
    () => new Set(orderedProfiles.map((p) => p.profileCode)),
    [orderedProfiles],
  );
  const onExportCsv = React.useCallback((): void => {
    const rows = edges.filter(
      (e) => visibleCodes.has(e.profileFrom) && visibleCodes.has(e.profileTo),
    );
    const header = 'profilSource,profilCible,correlationPourcent';
    const body = rows
      .map(
        (e) =>
          `${e.profileFrom},${e.profileTo},${String(correlationWeightToPercent(e.weight))}`,
      )
      .join('\n');
    saveCsv(`${header}\n${body}\n`, `matrice-correlation-${catalogVersionId}.csv`);
  }, [edges, visibleCodes, catalogVersionId]);

  const [showValues, setShowValues] = React.useState(false);

  // Cellule SURVOLÉE (readout + % dans la cellule) + cellule en ÉDITION EN CELLULE + brouillon saisi.
  const upsert = useUpsertEdge();
  const [hover, setHover] = React.useState<CellRef | null>(null);
  const [editing, setEditing] = React.useState<CellRef | null>(null);
  const [draft, setDraft] = React.useState('');
  const [parseError, setParseError] = React.useState<string | null>(null);

  const startEdit = React.useCallback(
    (from: string, to: string): void => {
      const current = getPercent(from, to);
      setEditing({ from, to });
      setDraft(current === null ? '' : String(current));
      setParseError(null);
      upsert.reset();
    },
    [getPercent, upsert],
  );
  const cancelEdit = React.useCallback((): void => {
    setEditing(null);
    setParseError(null);
  }, []);

  const confirmEdit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (editing === null) {
        return;
      }
      setParseError(null);
      const parsed = Number(draft);
      if (draft.trim().length === 0 || Number.isNaN(parsed)) {
        setParseError('La corrélation doit être un nombre.');
        return;
      }
      if (parsed < 0 || parsed > 100) {
        setParseError('La corrélation doit être comprise entre 0 et 100 %.');
        return;
      }
      const existing = edgeIndex.get(edgeKey(editing.from, editing.to));
      upsert.mutate(
        {
          catalogVersionId,
          input: {
            ...(existing !== undefined ? { edgeId: existing.edgeId } : {}),
            profileFrom: editing.from,
            profileTo: editing.to,
            // Conversion d'AFFICHAGE → contrat : le fil réseau reste 0-1 (INCHANGÉ).
            weight: correlationPercentToWeight(parsed),
          },
        },
        {
          onSuccess: () => {
            setEditing(null);
          },
        },
      );
    },
    [editing, draft, edgeIndex, upsert, catalogVersionId],
  );

  // Cellule affichée dans les cartes readout : celle en édition, sinon celle survolée.
  const displayed = editing ?? hover;
  const cardValue =
    editing !== null
      ? draft.trim().length === 0 || Number.isNaN(Number(draft))
        ? null
        : Math.round(Number(draft))
      : displayed !== null
        ? getPercent(displayed.from, displayed.to)
        : null;

  const isPending = profilesQuery.isPending || edgesQuery.isPending;
  const isError = profilesQuery.isError || edgesQuery.isError;
  const errorMessage =
    profilesQuery.error?.message ?? edgesQuery.error?.message ?? 'Erreur inconnue.';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Carte de chaleur des corrélations</CardTitle>
        <CardDescription>
          Profils en lignes et colonnes, groupés par famille. La couleur porte le taux de
          corrélation (0 à 100&nbsp;%). Survolez une cellule pour la prévisualiser, cliquez pour
          l’éditer dans le volet latéral, puis confirmez. Symétrie appliquée aux deux sens.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <MatrixHeaderBar
          familles={familles}
          view={view}
          onViewChange={onViewChange}
          showValues={showValues}
          onShowValuesChange={setShowValues}
          filledPairs={filledPairs}
          onExportCsv={onExportCsv}
          exportDisabled={edges.length === 0}
        />

        {isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement de la matrice…
          </p>
        ) : isError ? (
          <p role="alert" className="text-sm text-destructive">
            Erreur de chargement : {errorMessage}
          </p>
        ) : orderedProfiles.length === 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            {profiles.length === 0
              ? 'Aucun profil à corréler pour le moment.'
              : 'Aucun profil ne correspond aux filtres sélectionnés.'}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <PairCard displayed={displayed} byCode={byCode} />
              <ValueCard value={cardValue} active={displayed !== null} />
            </div>

            <HeatmapGrid
              familyGroups={familyGroups}
              orderedProfiles={orderedProfiles}
              getPercent={getPercent}
              degree={degree}
              showValues={showValues}
              hover={hover}
              editing={editing}
              draft={draft}
              parseError={parseError}
              pending={upsert.isPending}
              onStartEdit={startEdit}
              onDraftChange={setDraft}
              onConfirm={confirmEdit}
              onCancel={cancelEdit}
              onHover={setHover}
              onHoverEnd={() => {
                setHover(null);
              }}
            />

            <div aria-live="polite" aria-atomic="true">
              {parseError !== null ? (
                <p role="alert" className="text-sm text-destructive">
                  {parseError}
                </p>
              ) : upsert.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  {upsert.error.message}
                </p>
              ) : upsert.isSuccess ? (
                <p role="status" className="text-sm text-success">
                  Corrélation enregistrée
                </p>
              ) : null}
            </div>
          </div>
        )}

        <CorrelatedProfilesPanel profiles={profiles} edges={edges} />
      </CardContent>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Barre d'en-tête : compteur de paires, légende d'échelle, filtres, bascule des valeurs, export.
// -----------------------------------------------------------------------------

function MatrixHeaderBar({
  familles,
  view,
  onViewChange,
  showValues,
  onShowValuesChange,
  filledPairs,
  onExportCsv,
  exportDisabled,
}: {
  readonly familles: readonly string[];
  readonly view: CatalogMatrixView;
  readonly onViewChange: (next: CatalogMatrixView) => void;
  readonly showValues: boolean;
  readonly onShowValuesChange: (next: boolean) => void;
  readonly filledPairs: number;
  readonly onExportCsv: () => void;
  readonly exportDisabled: boolean;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="mr-auto space-y-1">
        <p className="text-sm font-medium tabular-nums text-foreground">
          {filledPairs} paire{filledPairs > 1 ? 's' : ''} renseignée
          {filledPairs > 1 ? 's' : ''}
        </p>
        <ScaleLegend />
      </div>

      <div className="space-y-1">
        <Label htmlFor="matrix-famille">Filtrer par famille</Label>
        <select
          id="matrix-famille"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          value={view.famille ?? ''}
          onChange={(e) => {
            const next = { ...view };
            if (e.target.value === '') {
              delete next.famille;
            } else {
              next.famille = e.target.value;
            }
            onViewChange(next);
          }}
        >
          <option value="">Toutes les familles</option>
          {familles.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>

      <span className="flex items-center gap-2 pb-2">
        <input
          id="matrix-actives"
          type="checkbox"
          className="h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          checked={view.actives === true}
          onChange={(e) => {
            const next = { ...view };
            if (e.target.checked) {
              next.actives = true;
            } else {
              delete next.actives;
            }
            onViewChange(next);
          }}
        />
        <Label htmlFor="matrix-actives">Masquer les profils sans corrélation</Label>
      </span>

      <span className="flex items-center gap-2 pb-2">
        <input
          id="matrix-values"
          type="checkbox"
          className="h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          checked={showValues}
          onChange={(e) => {
            onShowValuesChange(e.target.checked);
          }}
        />
        <Label htmlFor="matrix-values">Afficher les valeurs</Label>
      </span>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mb-2"
        onClick={onExportCsv}
        disabled={exportDisabled}
      >
        Exporter la matrice (CSV)
      </Button>
    </div>
  );
}

/** Légende d'échelle : 6 pastilles de la teinte unique, 0 % → 100 % (couleur = valeur). */
function ScaleLegend(): React.JSX.Element {
  const stops = [0, 20, 40, 60, 80, 100];
  return (
    <span className="flex items-center gap-1">
      <span className="text-xs text-muted-foreground">0&nbsp;%</span>
      <span aria-hidden="true" className="flex gap-0.5">
        {stops.map((v) => (
          <span
            key={v}
            className="inline-block rounded-sm border border-border/50"
            style={{ width: 16, height: 9, background: cellBackground(v) }}
          />
        ))}
      </span>
      <span className="text-xs text-muted-foreground">100&nbsp;%</span>
    </span>
  );
}

// -----------------------------------------------------------------------------
// Grille dense : gouttière collante (code + libellé), bandeau vertical de codes, cellules colorées.
// -----------------------------------------------------------------------------

const CELL_PX = 24;
const GUTTER_PX = 160;
const FAMILY_GAP_PX = 8;

function HeatmapGrid({
  familyGroups,
  orderedProfiles,
  getPercent,
  degree,
  showValues,
  hover,
  editing,
  draft,
  parseError,
  pending,
  onStartEdit,
  onDraftChange,
  onConfirm,
  onCancel,
  onHover,
  onHoverEnd,
}: {
  readonly familyGroups: readonly MatrixFamilyGroup[];
  readonly orderedProfiles: readonly ProfileView[];
  readonly getPercent: (from: string, to: string) => number | null;
  readonly degree: ReadonlyMap<string, number>;
  readonly showValues: boolean;
  readonly hover: CellRef | null;
  readonly editing: CellRef | null;
  readonly draft: string;
  readonly parseError: string | null;
  readonly pending: boolean;
  readonly onStartEdit: (from: string, to: string) => void;
  readonly onDraftChange: (next: string) => void;
  readonly onConfirm: (event: React.FormEvent<HTMLFormElement>) => void;
  readonly onCancel: () => void;
  readonly onHover: (cell: CellRef) => void;
  readonly onHoverEnd: () => void;
}): React.JSX.Element {
  const displayed = editing ?? hover;
  const cols = orderedProfiles;
  const familyIndexOf = React.useCallback(
    (famille: string): number => familyGroups.findIndex((g) => g.famille === famille),
    [familyGroups],
  );
  const isFamilyStart = (j: number): boolean =>
    j === 0 || cols[j - 1]?.famille !== cols[j]?.famille;
  // Un espaceur (colonne dédiée) précède chaque bloc famille sauf le premier : le gap inter-famille
  // ne DÉFORME PAS la taille des cellules (box-sizing border-box rétrécirait une cellule à bordure).
  const needsSpacer = (j: number): boolean => j > 0 && isFamilyStart(j);
  const spacerCount = cols.filter((_, j) => needsSpacer(j)).length;
  const fullColSpan = 1 + cols.length + spacerCount;

  // Ripiegamento par MACRO-FAMILLE (1er niveau), comme l'arborescence : masque les LIGNES du bloc.
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(() => new Set());
  const toggleFamily = React.useCallback((famille: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(famille)) {
        next.delete(famille);
      } else {
        next.add(famille);
      }
      return next;
    });
  }, []);

  const headerSpacer = (col: ProfileView, top: string): React.JSX.Element => (
    <td
      key={`spacer-${col.profileCode}`}
      aria-hidden="true"
      className={`sticky ${top} z-20 bg-card p-0`}
      style={{ width: FAMILY_GAP_PX, minWidth: FAMILY_GAP_PX }}
    />
  );
  const bodySpacer = (col: ProfileView): React.JSX.Element => (
    <td
      key={`spacer-${col.profileCode}`}
      aria-hidden="true"
      className="p-0"
      style={{ width: FAMILY_GAP_PX, minWidth: FAMILY_GAP_PX }}
    />
  );

  return (
    <div
      className="min-w-0 flex-1 overflow-auto rounded-lg border border-border"
      style={{ maxHeight: '72vh' }}
    >
      <table className="border-separate" style={{ borderSpacing: '1px' }}>
        <caption className="sr-only">
          Matrice de corrélation : profils en lignes et en colonnes, groupés par famille. La couleur
          d’une cellule porte le taux de corrélation de la ligne vers la colonne. Survolez pour voir
          la valeur dans la cellule, cliquez pour l’éditer en cellule.
        </caption>
        <thead>
          {/* Bandeau (décoratif) des codes courts de famille — <td> pour ne pas créer d'en-têtes vides. */}
          <tr aria-hidden="true">
            <td
              className="sticky left-0 top-0 z-30 bg-card"
              style={{ width: GUTTER_PX, minWidth: GUTTER_PX }}
            />
            {cols.map((col, j) => (
              <React.Fragment key={col.profileCode}>
                {needsSpacer(j) ? headerSpacer(col, 'top-0') : null}
                <td
                  className="sticky top-0 z-20 bg-card p-0 align-bottom"
                  style={{ width: CELL_PX, minWidth: CELL_PX }}
                >
                  {isFamilyStart(j) ? (
                    <span
                      className="block whitespace-nowrap px-0.5 font-mono text-[9px] font-semibold"
                      style={{ color: familyLabelColor(familyIndexOf(col.famille)) }}
                    >
                      {familyShortCode(col.famille)}
                    </span>
                  ) : null}
                </td>
              </React.Fragment>
            ))}
          </tr>
          {/* Bandeau des codes profil en écriture verticale. */}
          <tr>
            <th
              scope="col"
              className="sticky left-0 top-4 z-30 bg-card text-left align-bottom"
              style={{ width: GUTTER_PX, minWidth: GUTTER_PX }}
            >
              <span className="sr-only">Profil ligne vers profil colonne</span>
            </th>
            {cols.map((col, j) => {
              const highlighted = displayed?.to === col.profileCode;
              return (
                <React.Fragment key={col.profileCode}>
                  {needsSpacer(j) ? headerSpacer(col, 'top-4') : null}
                  <th
                    scope="col"
                    className={cn(
                      'sticky top-4 z-20 bg-card p-0 align-bottom',
                      highlighted && 'bg-accent',
                    )}
                    style={{ width: CELL_PX, minWidth: CELL_PX, height: 44 }}
                  >
                    <span
                      aria-hidden="true"
                      className="mx-auto block font-mono text-[8px] text-muted-foreground"
                      style={{ writingMode: 'vertical-rl' }}
                    >
                      {codeSuffix(col.profileCode)}
                    </span>
                    <span className="sr-only">
                      {col.libelle}. {degreeLabel(degree.get(col.profileCode) ?? 0)}
                    </span>
                  </th>
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {familyGroups.map((group) => (
            <React.Fragment key={group.famille}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={fullColSpan}
                  className="sticky left-0 z-10 bg-muted/40 p-0"
                >
                  <button
                    type="button"
                    aria-expanded={!collapsed.has(group.famille)}
                    onClick={() => {
                      toggleFamily(group.famille);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  >
                    <ChevronRight
                      aria-hidden="true"
                      className={cn(
                        'size-4 shrink-0 text-muted-foreground transition-transform duration-150',
                        !collapsed.has(group.famille) && 'rotate-90',
                      )}
                    />
                    <Folder aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">{group.famille}</span>
                    <Badge variant="secondary" className="ml-0.5">
                      <span aria-hidden="true">{group.profiles.length}</span>
                      <span className="sr-only">
                        {group.profiles.length > 1
                          ? `${String(group.profiles.length)} profils`
                          : '1 profil'}
                      </span>
                    </Badge>
                  </button>
                </th>
              </tr>
              {collapsed.has(group.famille)
                ? null
                : group.profiles.map((row) => {
                const rowHighlighted = displayed?.from === row.profileCode;
                return (
                  <tr key={row.profileCode}>
                    <th
                      scope="row"
                      className={cn(
                        'sticky left-0 z-10 bg-card pr-1 text-left align-middle',
                        rowHighlighted && 'bg-accent',
                      )}
                      style={{ width: GUTTER_PX, minWidth: GUTTER_PX, maxWidth: GUTTER_PX }}
                    >
                      <span className="flex items-baseline gap-1 overflow-hidden">
                        <span className="shrink-0 font-mono text-[8.5px] text-muted-foreground">
                          {row.profileCode}
                        </span>
                        <span className="truncate text-[10px]" title={row.libelle}>
                          {row.libelle}
                        </span>
                      </span>
                      <span className="sr-only">
                        {degreeLabel(degree.get(row.profileCode) ?? 0)}
                      </span>
                    </th>
                    {cols.map((col, j) => {
                      if (row.profileCode === col.profileCode) {
                        return (
                          <React.Fragment key={col.profileCode}>
                            {needsSpacer(j) ? bodySpacer(col) : null}
                            <td
                              className="p-0"
                              style={{
                                width: CELL_PX,
                                minWidth: CELL_PX,
                                height: CELL_PX,
                                backgroundImage: DIAGONAL_HATCH,
                              }}
                            >
                              <span className="sr-only">Même profil, non applicable</span>
                            </td>
                          </React.Fragment>
                        );
                      }
                      const percent = getPercent(row.profileCode, col.profileCode);
                      const isEditing = sameCell(editing, {
                        from: row.profileCode,
                        to: col.profileCode,
                      });
                      const isHovered = sameCell(hover, {
                        from: row.profileCode,
                        to: col.profileCode,
                      });
                      return (
                        <React.Fragment key={col.profileCode}>
                          {needsSpacer(j) ? bodySpacer(col) : null}
                          <td
                            className="relative p-0"
                            style={{ width: CELL_PX, minWidth: CELL_PX, height: CELL_PX }}
                          >
                          <button
                            type="button"
                            onClick={() => {
                              onStartEdit(row.profileCode, col.profileCode);
                            }}
                            onMouseEnter={() => {
                              onHover({ from: row.profileCode, to: col.profileCode });
                            }}
                            onMouseLeave={onHoverEnd}
                            className={cn(
                              'flex h-full w-full items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                              isEditing && 'outline outline-2 outline-foreground',
                            )}
                            style={{
                              background: cellBackground(percent),
                              color: percent !== null ? cellTextColor(percent) : undefined,
                            }}
                            aria-label={
                              percent !== null
                                ? `Corrélation de ${row.libelle} vers ${col.libelle} : ${String(percent)} %`
                                : `Corrélation de ${row.libelle} vers ${col.libelle} : non définie`
                            }
                          >
                            {/* Le survol révèle la valeur DANS la cellule (le toggle l'affiche en permanence). */}
                            {(showValues || isHovered) && percent !== null ? (
                              <span className="text-[9px] leading-none tabular-nums">
                                {percent}
                              </span>
                            ) : null}
                          </button>
                          {isEditing ? (
                            <CellEditor
                              rowLabel={row.libelle}
                              colLabel={col.libelle}
                              value={draft}
                              onValueChange={onDraftChange}
                              onConfirm={onConfirm}
                              onCancel={onCancel}
                              invalid={parseError !== null}
                              pending={pending}
                            />
                          ) : null}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Cartes readout (au-dessus de la grille) + éditeur EN CELLULE.
// -----------------------------------------------------------------------------

/** Carte n°1 : la PAIRE de profils de la cellule survolée / en édition (readout, non éditable). */
function PairCard({
  displayed,
  byCode,
}: {
  readonly displayed: CellRef | null;
  readonly byCode: ReadonlyMap<string, ProfileView>;
}): React.JSX.Element {
  const rowProfile = displayed !== null ? byCode.get(displayed.from) : undefined;
  const colProfile = displayed !== null ? byCode.get(displayed.to) : undefined;
  return (
    <div className="min-w-[260px] flex-1 rounded-lg border border-border p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Paire de profils
      </p>
      {rowProfile === undefined || colProfile === undefined ? (
        <p className="mt-1 text-sm text-muted-foreground">
          Survolez ou cliquez une cellule de la grille.
        </p>
      ) : (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span>
            <span className="block font-mono text-xs text-muted-foreground">
              {rowProfile.profileCode}
            </span>
            <span className="font-medium">{rowProfile.libelle}</span>
          </span>
          <span aria-hidden="true" className="text-muted-foreground">
            ↔
          </span>
          <span>
            <span className="block font-mono text-xs text-muted-foreground">
              {colProfile.profileCode}
            </span>
            <span className="font-medium">{colProfile.libelle}</span>
          </span>
        </div>
      )}
    </div>
  );
}

/** Carte n°2 : la VALEUR de la cellule survolée / en édition (readout ; symétrie rappelée). */
function ValueCard({
  value,
  active,
}: {
  readonly value: number | null;
  readonly active: boolean;
}): React.JSX.Element {
  return (
    <div className="w-52 shrink-0 rounded-lg border border-border p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Valeur
      </p>
      {active && value !== null ? (
        <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
          {value}
          <span className="text-base font-normal text-muted-foreground">&nbsp;%</span>
        </p>
      ) : (
        <p className="mt-1 text-3xl font-semibold text-muted-foreground">—</p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Symétrie appliquée aux deux sens</p>
    </div>
  );
}

/** Éditeur EN CELLULE (popover ancré à la cellule) : input % + confirmation explicite (l.965). */
function CellEditor({
  rowLabel,
  colLabel,
  value,
  onValueChange,
  onConfirm,
  onCancel,
  invalid,
  pending,
}: {
  readonly rowLabel: string;
  readonly colLabel: string;
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly onConfirm: (event: React.FormEvent<HTMLFormElement>) => void;
  readonly onCancel: () => void;
  readonly invalid: boolean;
  readonly pending: boolean;
}): React.JSX.Element {
  // Focus programmatique à l'ouverture (équivaut à autoFocus sans violer jsx-a11y/no-autofocus) :
  // l'éditeur EN CELLULE s'ouvre au clic explicite de l'utilisateur, la mise au focus est attendue.
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  return (
    <form
      onSubmit={onConfirm}
      className="absolute left-0 top-0 z-30 flex items-center gap-1 rounded-md border border-border bg-card p-1 shadow-md"
    >
      {/*
        Pas de min/max HTML natifs : la garde 0-100 est portée par le handler JS (message en %,
        non-autoritaire — le serveur reste l'autorité finale). Nom accessible contextuel ligne × colonne.
      */}
      <input
        ref={inputRef}
        type="number"
        inputMode="numeric"
        step={1}
        aria-label={`Taux de corrélation de ${rowLabel} vers ${colLabel}, en pourcentage`}
        aria-invalid={invalid}
        className="h-7 w-14 rounded border border-input px-1 text-center text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        value={value}
        onChange={(e) => {
          onValueChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <Button type="submit" size="sm" className="h-7 px-2" disabled={pending}>
        {pending ? '…' : 'Confirmer'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        onClick={onCancel}
      >
        Annuler
      </Button>
    </form>
  );
}

// -----------------------------------------------------------------------------
// Profils corrélés : suggestions cliquables basées sur la matrice (DBU §2.4 l.947).
// -----------------------------------------------------------------------------

const CORRELATED_SELECT_CLASS =
  'h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * Vue de VISUALISATION des profils corrélés (DBU §2.4 l.947, C4). LECTURE SEULE : on ne présente
 * PAS tous les profils à plat, mais des champs de recherche (libellé / famille / sous-famille) pour
 * SÉLECTIONNER un profil ; le résultat liste l'ensemble des profils corrélés au profil choisi + le
 * niveau de corrélation (%). Aucune édition ici (l'édition se fait dans la carte de chaleur).
 */
function CorrelatedProfilesPanel({
  profiles,
  edges,
}: {
  readonly profiles: readonly ProfileView[];
  readonly edges: readonly CorrelationEdgeView[];
}): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  const [selected, setSelected] = React.useState('');

  const byCode = React.useMemo(() => {
    const m = new Map<string, ProfileView>();
    for (const p of profiles) {
      m.set(p.profileCode, p);
    }
    return m;
  }, [profiles]);

  // Profils candidats du sélecteur : recherche unique sur libellé / famille / sous-famille / code.
  const candidates = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') {
      return profiles;
    }
    return profiles.filter(
      (p) =>
        p.libelle.toLowerCase().includes(q) ||
        p.famille.toLowerCase().includes(q) ||
        p.sousFamille.toLowerCase().includes(q) ||
        p.profileCode.toLowerCase().includes(q),
    );
  }, [profiles, query]);

  // Profils corrélés au profil sélectionné : voisins DISTINCTS (deux orientations), poids max
  // par voisin, triés décroissant. Read-only — aucune mutation.
  const correlated = React.useMemo(() => {
    if (selected === '') {
      return [];
    }
    const weightByNeighbor = new Map<string, number>();
    const bump = (code: string, weight: number): void => {
      if (byCode.has(code)) {
        weightByNeighbor.set(code, Math.max(weightByNeighbor.get(code) ?? 0, weight));
      }
    };
    for (const edge of edges) {
      if (edge.profileFrom === selected) {
        bump(edge.profileTo, edge.weight);
      } else if (edge.profileTo === selected) {
        bump(edge.profileFrom, edge.weight);
      }
    }
    return [...weightByNeighbor.entries()]
      .map(([code, weight]) => ({ profile: byCode.get(code), weight }))
      .filter((r): r is { profile: ProfileView; weight: number } => r.profile !== undefined)
      .sort((a, b) => b.weight - a.weight);
  }, [selected, edges, byCode]);

  const selectedProfile = selected === '' ? undefined : byCode.get(selected);

  return (
    <section aria-labelledby="correlated-heading" className="space-y-4">
      <div className="space-y-1">
        <h3 id="correlated-heading" className="text-sm font-medium text-foreground">
          Profils corrélés — visualisation
        </h3>
        <p className="text-xs text-muted-foreground">
          Recherchez et sélectionnez un profil pour afficher ses profils corrélés et leur niveau
          de corrélation. Vue en lecture seule.
        </p>
      </div>

      <div
        className="flex flex-wrap items-end gap-3"
        role="search"
        aria-label="Rechercher un profil corrélé"
      >
        <div className="min-w-[220px] flex-1 space-y-1">
          <Label htmlFor="corr-query">Rechercher un profil</Label>
          <Input
            id="corr-query"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Libellé, famille, sous-famille ou code…"
          />
        </div>
        <div className="min-w-[220px] flex-1 space-y-1">
          <Label htmlFor="corr-select">Profil</Label>
          <select
            id="corr-select"
            className={CORRELATED_SELECT_CLASS}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Sélectionner un profil</option>
            {candidates.map((p) => (
              <option key={p.profileCode} value={p.profileCode}>
                {p.libelle} ({p.famille} / {p.sousFamille})
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProfile === undefined ? null : correlated.length === 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          Aucun profil corrélé à «&nbsp;{selectedProfile.libelle}&nbsp;».
        </p>
      ) : (
        <ul className="space-y-2" aria-label={`Profils corrélés à ${selectedProfile.libelle}`}>
          {correlated.map(({ profile, weight }) => {
            const percent = correlationWeightToPercent(weight);
            const major = percent > MAJOR_CORRELATION_PERCENT;
            return (
              <li
                key={profile.profileCode}
                className="flex flex-wrap items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="font-medium">{profile.libelle}</span>
                <span className="text-muted-foreground">
                  {profile.famille} / {profile.sousFamille}
                </span>
                <span className="ml-auto font-medium tabular-nums">{percent}&nbsp;%</span>
                {major ? <Badge variant="accent">majeure</Badge> : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
